import React, { memo, useLayoutEffect, useRef, useState } from "react";
import { styled } from "styled-components";
import { ChevronLeft, ChevronRight } from "lucide-react";

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

const NavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background-color: ${({ theme }) => theme.colorSurface};
  color: ${({ theme }) => theme.colorOnSurface};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover:not(:disabled) {
    opacity: 0.8;
  }
  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
`;

const PrevButton = styled(NavButton)`
  left: 16px;
`;

const NextButton = styled(NavButton)`
  right: 16px;
`;

const StepImages = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${({ $single }) => $single ? "center" : "start"};
  gap: 10px;
  border-radius: 8px;
  padding: 0px 12px;
  overflow-x: scroll;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  -ms-overflow-style: none;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    display: none;
  }
  & > * {
    scroll-snap-align: center;
    scroll-snap-stop: always;
    flex-shrink: 0;
  }
`;

const StepImage = styled.img`
  height: ${({ $height }) => $height ?? 120}px;
  width: auto;
  border-radius: 8px;
  cursor: ${({ $clickable }) => $clickable ? "pointer" : "default"};
`;

const RecipeStepImages = memo(function ({ images, imageHeight, initialImageIndex = 0, onImageClick }) {
  const scrollRef = useRef();
  const [currentIndex, setCurrentIndex] = useState(initialImageIndex);

  useLayoutEffect(() => {
    if (!initialImageIndex) return;
    const container = scrollRef.current;
    if (!container) return;
    const child = container.children[initialImageIndex];
    if (child) child.scrollIntoView({ behavior: "instant", block: "nearest", inline: "center" });
  }, [initialImageIndex]);

  const scrollToIndex = (index) => {
    const container = scrollRef.current;
    if (!container) return;
    const child = container.children[index];
    if (child)
      child.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
  };

  const handleScroll = () => {
    const container = scrollRef.current;
    if (!container) return;
    const containerCenter = container.scrollLeft + container.offsetWidth / 2;
    let closest = 0;
    let minDist = Infinity;
    Array.from(container.children).forEach((child, i) => {
      const dist = Math.abs(child.offsetLeft + child.offsetWidth / 2 - containerCenter);
      if (dist < minDist) { minDist = dist; closest = i; }
    });
    setCurrentIndex(closest);
  };

  return (
    <Wrapper>
      {images.length > 1 && (
        <>
          <PrevButton
            onClick={() => scrollToIndex(currentIndex - 1)}
            disabled={currentIndex === 0}
          >
            <ChevronLeft size={16} />
          </PrevButton>
          <NextButton
            onClick={() => scrollToIndex(currentIndex + 1)}
            disabled={currentIndex === images.length - 1}
          >
            <ChevronRight size={16} />
          </NextButton>
        </>
      )}
      <StepImages ref={scrollRef} onScroll={handleScroll} $single={images.length === 1}>
        {images.map((image, index) => (
          <StepImage
            key={index}
            src={image}
            $height={imageHeight}
            $clickable={!!onImageClick}
            onClick={() => onImageClick?.(index)}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ))}
      </StepImages>
    </Wrapper>
  );
});
export default RecipeStepImages;
